import React from 'react';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import parseUrl from '@lib/prismic/util/parseUrl';
import breakpoints from '@constants/breakpoints';
import { useTheme } from '@components/theme';
import { Swiper, SwiperSlide } from 'swiper/react';
import classNames from 'classnames';
import PrismicImage from '@components/shared/images/atoms/Image/PrismicImage';
import SwiperCore, { Pagination } from 'swiper';
import { hashCode } from '@lib/string';
import { createPrismicImage, IPrismicImage } from '@lib/prismic/types';
import { useMaxBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import styles from './OurStories.module.scss';

SwiperCore.use([Pagination]);

export interface IBlogLink {
  image: IPrismicImage | string; // prismic image or image url
  description: string;
  url: string;
}

export interface IOurStoriesProps {
  className?: string;
  blogLinks: IBlogLink[];
}

const BlogPost = ({
  description,
  image,
  url
}: IBlogLink) => {
  let prismicImage: IPrismicImage;
  if (typeof image === 'string') {
    prismicImage = createPrismicImage({
      url: image,
      altText: description || '',
      dimensions: { width: 850, height: 580 },
      mobileDimensions: { width: 750, height: 516 }
    });
  } else {
    prismicImage = image;
  }

  return (
    <div className={styles.OurStoriesCard}>
      <a
        key={hashCode(description)}
        href={parseUrl(url)}
        onClick={() => {
          analyticsTrack('blog_article_clicked', {
            title: `Shop Landing Page | Our Stories`,
            description,
            image
          });
        }}
        role="button"
      >
        <div className={styles.ImageWrapper}>
          <PrismicImage
            image={prismicImage}
            lazy={false}
            breakpoints={{
              mobile: breakpoints.desktop,
            }}
          />
        </div>
        <div className={styles.CardDetails}>
          <div className={styles.CardDescription}>
            {description}
          </div>
        </div>
      </a>
    </div>
  );
};

const OurStories = ({ blogLinks, className }: IOurStoriesProps) => {
  const theme = useTheme();
  const isMobile = useMaxBreakpoint(breakpoints.desktop);

  return (
    <section className={classNames(className, styles.OurStories)}>
      <h2 className={theme.styles.Title1}>Our Stories</h2>

      <div className={classNames(styles.BlogPosts, theme.styles.Carousel)}>
        <div className={styles.Pagination} />
        <Swiper
          slidesPerView={1}
          spaceBetween={0}
          loop={isMobile}
          pagination={{
            type: 'bullets',
            el: `.${styles.BlogPosts} .${styles.Pagination}`,
            bulletClass: styles.Bullet,
            bulletActiveClass: styles.BulletActive,
          }}
          breakpoints={{
            [breakpoints['small-tablet']]: {
              slidesPerView: 2.2,
              spaceBetween: 20,
            },
            [breakpoints.desktop]: {
              slidesPerView: 3,
              spaceBetween: 40,
            }
          }}
        >
          {blogLinks.map((link) => (
            <SwiperSlide key={hashCode(link.description)}>
              <BlogPost {...link} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default OurStories;
